<template>
  <div class="exam-create-container">
    <div class="exam-create-content" v-loading="loading">
      <el-alert
        v-if="isCreatorWorkspace"
        type="info"
        :closable="false"
        show-icon
        title="创建后需管理员审核通过，才会出现在公开测评列表。您可随时在「我的创作」中预览、试测与查看结果。"
        style="margin-bottom: 16px"
      />
      <el-form :model="exam" :rules="rules" ref="examForm" class="exam-form" label-width="120px">
        <el-form-item label="测评名称" prop="examName">
          <el-input v-model="exam.examName" placeholder="请输入测评名称"></el-input>
        </el-form-item>

        <el-form-item label="英文简称" prop="examSname">
          <el-input
            v-model="exam.examSname"
            placeholder="如 ocean-60、via-24（字母开头，可含数字与连字符，非空时不可重复）"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="内容标签" prop="tagIds">
          <ContentTagSelect v-model="exam.tagIds" scope="exam" />
          <div class="field-hint">从系统字典多选，不能自定义新建。题目 JSON 的 question_tag 是计分维度，不要写在这里。</div>
        </el-form-item>

        <el-form-item label="测评简介" prop="profile">
          <el-input
            type="textarea"
            v-model="exam.profile"
            :rows="3"
            placeholder="请输入测评简介"
          ></el-input>
        </el-form-item>

        <el-form-item label="详细说明" prop="description" class="description-editor-item">
          <MonoEdit v-model="exam.description" height="min(56vh, 640px)" />
        </el-form-item>

        <el-form-item label="测评反馈" prop="feedbacks" class="json-editor-item">
          <MonoJsonEdit v-model="exam.feedbacks" field-label="测评反馈" height="min(36vh, 400px)" />
          <div class="field-hint">JSON，留空表示无反馈配置</div>
        </el-form-item>

        <el-form-item label="测评总结" prop="summarys" class="json-editor-item">
          <MonoJsonEdit v-model="exam.summarys" field-label="测评总结" height="min(36vh, 400px)" />
          <div class="field-hint">JSON，留空表示无总结配置</div>
        </el-form-item>

        <el-form-item label="题目数量" prop="questionCount">
          <el-input-number v-model="exam.questionCount" :min="1" :max="100"></el-input-number>
        </el-form-item>

        <el-form-item label="限时（分钟）" prop="timeLimit">
          <el-input-number v-model="exam.timeLimit" :min="0" :max="180"></el-input-number>
        </el-form-item>

        <el-form-item label="永久免费" prop="free">
          <el-switch v-model="exam.free" :active-value="1" :inactive-value="0" />
          <div class="field-hint">开启后前台显示「免费」角标</div>
        </el-form-item>

        <el-form-item label="成交价（元）" prop="fee">
          <el-input-number v-model="exam.fee" :min="0" :precision="2" style="width: 100%" />
          <div class="field-hint">实际扣费价格（单位：元，保存为分）</div>
        </el-form-item>

        <el-form-item label="原价（元）" prop="originFee">
          <el-input-number v-model="exam.originFee" :min="0" :precision="2" style="width: 100%" />
          <div class="field-hint">
            划线价。限时免费：永久免费关 + 成交价 0 + 原价 &gt; 0；限时优惠：0 &lt; 成交价 &lt; 原价
          </div>
        </el-form-item>

        <el-form-item label="封面图片" prop="cover">
          <el-upload
            class="cover-uploader"
            action="/api/v1/upload"
            :headers="uploadHeaders"
            :show-file-list="false"
            :data="uploadData"
            :on-success="handleCoverSuccess"
            :before-upload="beforeCoverUpload"
          >
            <img v-if="exam.cover" :src="exam.cover" class="cover" />
            <el-icon v-else class="cover-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="缩略图" prop="thumb">
          <el-upload
            class="thumb-uploader"
            action="/api/v1/upload"
            :headers="uploadHeaders"
            :show-file-list="false"
            :data="uploadData"
            :on-success="handleThumbSuccess"
            :before-upload="beforeThumbUpload"
          >
            <img v-if="exam.thumb" :src="exam.thumb" class="thumb" />
            <el-icon v-else class="thumb-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="买一赠一" prop="oneFree">
          <el-switch v-model="exam.oneFree" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item label="评价者ID" prop="evaluatorUserIds">
          <el-input v-model="exam.evaluatorUserIds" placeholder="请输入评价者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="被评者ID" prop="evaluateeUserIds">
          <el-input v-model="exam.evaluateeUserIds" placeholder="请输入被评者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="统计平均分数查看者" prop="reportAverageUserIds">
          <el-input v-model="exam.reportAverageUserIds" placeholder="请输入可查看统计平均分数者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="详细分数查看者" prop="reportDetailUserIds">
          <el-input v-model="exam.reportDetailUserIds" placeholder="请输入可查看详细分数者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="题目 Key 集合" prop="questionKeys" class="json-editor-item">
          <MonoJsonEdit v-model="exam.questionKeys" field-label="题目 Key 集合" height="min(28vh, 320px)" />
        </el-form-item>

        <el-form-item label="原始题目" prop="originalQuestions" class="json-editor-item">
          <MonoJsonEdit
            ref="originalQuestionsEditor"
            v-model="exam.originalQuestions"
            field-label="原始题目"
            height="min(56vh, 640px)"
          />
          <div class="field-hint">
            保存后由服务端根据分值与统计维度生成「答题卷」(questions) 与 max_score，无需单独编辑所有题目。
          </div>
        </el-form-item>

        <el-form-item label="分类ID" prop="categoryId">
          <el-input-number v-model="exam.categoryId" :min="0"></el-input-number>
        </el-form-item>

        <el-form-item label="试题中断提示" prop="stops" class="json-editor-item">
          <MonoJsonEdit v-model="exam.stops" field-label="试题中断提示" height="min(28vh, 320px)" />
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="是否热门" prop="isHot">
          <el-switch v-model="exam.isHot" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="首页展示" prop="homeDisplay">
          <el-switch v-model="exam.homeDisplay" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="submitForm">创建</el-button>
          <el-button @click="cancel">取消</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { validatePhotoUpload, adminUploadFormData, adminUploadHeaders, uploadResponseToUrl } from '@/utils/photoUpload'
import MonoEdit from './components/MonoEdit.vue'
import MonoJsonEdit from '@/components/MonoJsonEdit.vue'
import { Plus } from '@element-plus/icons-vue'
import authManager from '@/utils/auth'
import { buildExamSubmitPayload } from '@/utils/examPricing'
import { bumpExamListCache } from '@/utils/listPageCache'
import { normalizeExamJsonFields } from '@/utils/examJson'
import ContentTagSelect from '@/components/ContentTagSelect.vue'

export default {
  name: 'ExamCreateView',
  components: {
    MonoEdit,
    MonoJsonEdit,
    Plus,
    ContentTagSelect
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const isCreatorWorkspace = computed(() => route.meta.creatorExam === true)
    const examForm = ref(null)
    const loading = ref(false)

    // Print current user's permissions
    const printUserPermissions = async () => {
      try {
        const userInfo = await authManager.getUserInfo()
        console.log('Current user role:', userInfo?.sysRole)
      } catch (err) {
        console.error('Failed to get user permissions:', err)
      }
    }

    // Call it when component is created
    printUserPermissions()

    const examSnamePattern = /^[a-zA-Z][a-zA-Z0-9]*(-[a-zA-Z0-9]+)*$/

    const exam = ref({
      examName: '',
      examSname: '',
      profile: '',
      description: '',
      feedbacks: '',
      summarys: '',
      questionCount: 1,
      timeLimit: 0,
      free: 0,
      fee: 0,
      originFee: 0,
      cover: '',
      thumb: '',
      questionKeys: '',
      originalQuestions: '',
      oneFree: 0,
      evaluatorUserIds: '',
      evaluateeUserIds: '',
      reportAverageUserIds: '',
      reportDetailUserIds: '',
      categoryId: 0,
      stops: '',
      isHot: 0,
      homeDisplay: 0,
      tagIds: []
    })

    const rules = {
      examName: [
        { required: true, message: '请输入测评名称', trigger: 'blur' }
      ],
      examSname: [
        {
          validator: (_rule, value, callback) => {
            const s = (value || '').trim()
            if (!s) {
              callback()
              return
            }
            if (!examSnamePattern.test(s)) {
              callback(new Error('格式如 ocean-60、via-24：字母开头，仅字母数字与连字符'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ],
      questionCount: [
        { required: true, message: '请输入题目数量', trigger: 'blur' }
      ],
      timeLimit: [
        { required: true, message: '请输入限时时间', trigger: 'blur' }
      ],
      originalQuestions: [
        { required: true, message: '请输入原始题目 JSON', trigger: 'blur' }
      ]
    }

    const uploadHeaders = computed(() => adminUploadHeaders())
    const uploadData = computed(() => adminUploadFormData('exam'))

    const handleCoverSuccess = (response) => {
      if (response?.code === 200) {
        exam.value.cover = uploadResponseToUrl(response)
        ElMessage.success('封面图上传成功')
      } else {
        ElMessage.error(response?.message || '上传失败')
      }
    }

    const handleThumbSuccess = (response) => {
      if (response?.code === 200) {
        exam.value.thumb = uploadResponseToUrl(response)
        ElMessage.success('缩略图上传成功')
      } else {
        ElMessage.error(response?.message || '上传失败')
      }
    }

    const beforeCoverUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const beforeThumbUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const submitForm = async () => {
      if (!examForm.value) return
      
      await examForm.value.validate(async (valid) => {
        if (valid) {
          loading.value = true
          try {
            const normalized = normalizeExamJsonFields(exam.value)
            const postData = buildExamSubmitPayload(normalized, { forUpdate: false, feeInYuan: true })

            const token = await authManager.getToken()
            const response = await axios.post('/api/v1/exam/create', postData, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            })
            if (response.data && response.data.data) {
              ElMessage.success(isCreatorWorkspace.value ? '创建成功，请等待管理员审核' : '创建成功')
              bumpExamListCache()
              if (isCreatorWorkspace.value) {
                router.push({ path: '/creator/works', query: { tab: 'exam' } })
              } else {
                router.push(`/admin/exam/${response.data.data.id}`)
              }
            }
          } catch (err) {
            console.error('Failed to create exam:', err)
            ElMessage.error(err.response?.data?.message || '创建失败')
          } finally {
            loading.value = false
          }
        }
      })
    }

    const cancel = () => {
      if (isCreatorWorkspace.value) {
        router.push({ path: '/creator/works', query: { tab: 'exam' } })
      } else {
        router.back()
      }
    }

    return {
      isCreatorWorkspace,
      exam,
      rules,
      loading,
      examForm,
      uploadHeaders,
      uploadData,
      handleCoverSuccess,
      handleThumbSuccess,
      beforeCoverUpload,
      beforeThumbUpload,
      submitForm,
      cancel
    }
  }
}
</script>

<style scoped>
.exam-create-container {
  width: 100%;
  max-width: none;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.exam-create-content {
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.exam-form {
  width: 100%;
}

.exam-form :deep(.el-form-item__content) {
  flex: 1;
  min-width: 0;
}

.description-editor-item :deep(.el-form-item__content),
.json-editor-item :deep(.el-form-item__content) {
  line-height: normal;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
}

.exam-form :deep(.mono-json-edit),
.exam-form :deep(.mono-edit) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.field-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.cover-uploader,
.thumb-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 178px;
  height: 178px;
}

.cover-uploader:hover,
.thumb-uploader:hover {
  border-color: #409EFF;
}

.cover-uploader-icon,
.thumb-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}

.cover,
.thumb {
  width: 178px;
  height: 178px;
  display: block;
}

</style> 